import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3, LineChart, PieChart, Table as TableIcon, Palette, Tag, Ruler,
  ArrowDownUp, Hash, Flag, Database, Pin, Undo2, MessageCircle, Layers,
  Download, Bookmark,
} from 'lucide-react';

// ChatGPT/Notion-style "/" command palette for AI Explore. Everything maps to
// the SAME plain-English command grammar (parseVizCommand), so typing and the
// menu stay in sync. Context-aware: only actions valid for the current chart
// appear, and "Colour by …" lists the real dimensions in the result.
const GROUP_ICON = {
  'Chart type': BarChart3, 'Series & colour': Palette, 'Labels & legend': Tag,
  'Axes & scale': Ruler, 'Sorting & limits': ArrowDownUp, 'Formatting': Hash,
  'Reference lines': Flag, 'Data': Database, 'Export': Download, 'Presets': Bookmark,
};

function buildItems(ctx) {
  const { hasChart, dims = [], chartType = '', presets = [] } = ctx || {};
  const pieLike = /pie|donut|gauge|funnel/.test(chartType);
  const items = [];
  const add = (group, label, kind, payload) => items.push({ group, label, kind, payload });

  if (hasChart) {
    add('Chart type', 'Bar chart', 'apply', 'make it a bar chart');
    add('Chart type', 'Column chart', 'apply', 'make it a column chart');
    add('Chart type', 'Line chart', 'apply', 'make it a line chart');
    add('Chart type', 'Area chart', 'apply', 'make it an area chart');
    add('Chart type', 'Pie chart', 'apply', 'make it a pie chart');
    add('Chart type', 'Donut chart', 'apply', 'make it a donut chart');
  }
  add('Chart type', 'Table', 'action', 'table');

  if (hasChart && !pieLike) {
    for (const d of dims) add('Series & colour', `Colour by ${d.label}`, 'apply', `colour by ${d.name}`);
    add('Series & colour', 'Merge into one series', 'apply', 'merge into one line');
  }

  if (hasChart) {
    add('Labels & legend', 'Show data labels', 'apply', 'show data labels');
    add('Labels & legend', 'Hide data labels', 'apply', 'hide data labels');
    add('Labels & legend', 'Show legend', 'apply', 'show legend');
    add('Labels & legend', 'Hide legend', 'apply', 'hide legend');
    add('Labels & legend', 'Show gridlines', 'apply', 'show gridlines');
    add('Labels & legend', 'Hide gridlines', 'apply', 'hide gridlines');
    add('Labels & legend', 'Hide axis titles', 'apply', 'hide axis titles');

    add('Axes & scale', 'Start axis at zero', 'apply', 'start the axis at zero');
    add('Axes & scale', 'Log scale', 'apply', 'log scale');
    add('Axes & scale', 'Linear scale', 'apply', 'linear scale');
    add('Axes & scale', 'Show as % of total', 'apply', 'show as percent of total');

    add('Sorting & limits', 'Sort highest first', 'apply', 'sort descending');
    add('Sorting & limits', 'Sort lowest first', 'apply', 'sort ascending');
    add('Sorting & limits', 'Top N…', 'fill', 'top ');
    add('Sorting & limits', 'Show all rows', 'apply', 'show all rows');

    add('Formatting', 'Show in millions', 'apply', 'show in millions');
    add('Formatting', 'Show in thousands', 'apply', 'show in thousands');
    add('Formatting', 'Round to whole numbers', 'apply', 'round to whole numbers');
    add('Formatting', 'Abbreviate large numbers', 'apply', 'abbreviate large numbers');

    add('Reference lines', 'Add average line', 'apply', 'add an average line');
    add('Reference lines', 'Target line at…', 'fill', 'target line at ');
    add('Reference lines', 'Remove reference line', 'apply', 'remove the reference line');
  }

  add('Data', 'Pin to dashboard', 'action', 'pin');
  add('Data', 'Undo last change', 'action', 'undo');
  add('Data', 'Explain this', 'action', 'explain');
  add('Data', 'Ask a question', 'action', 'ask');

  if (hasChart) {
    add('Export', 'Export data (CSV)', 'action', 'export-csv');
    add('Export', 'Export image (PNG)', 'action', 'export-png');
    add('Presets', 'Save current view as preset…', 'action', 'preset-save');
    for (const p of presets) add('Presets', `Apply: ${p.name}`, 'action', `preset-apply:${p.preset_id}`);
  }
  return items;
}

export function SlashCommandMenu({ query, context, onApply, onFill, onAction, onClose, recent = [] }) {
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const items = useMemo(() => buildItems(context), [context]);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    let base = items;
    if (q) base = items.filter(it => it.label.toLowerCase().includes(q) || it.group.toLowerCase().includes(q));
    // Smart suggestions: pin recently-used commands to the top when not filtering.
    if (!q && recent.length) {
      const recentItems = recent
        .map(r => items.find(it => it.kind === r.kind && String(it.payload) === String(r.payload)))
        .filter(Boolean);
      const rest = base.filter(it => !recentItems.includes(it));
      base = [...recentItems.map(it => ({ ...it, group: 'Recent' })), ...rest];
    }
    return base;
  }, [items, query, recent]);

  useEffect(() => { setActive(0); }, [query]);

  const pick = (it) => {
    if (!it) return;
    if (it.kind === 'apply') onApply(it.payload, it);
    else if (it.kind === 'fill') onFill(it.payload);
    else if (it.kind === 'action') onAction(it.payload, it);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); pick(filtered[active]); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose && onClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!filtered.length) {
    return (
      <div className="absolute bottom-full mb-2 left-0 right-0 z-40 rounded-xl border border-border/50 bg-popover shadow-2xl p-3 text-[11px] text-muted-foreground" data-testid="slash-menu-empty">
        No matching commands. Keep typing to ask a question instead.
      </div>
    );
  }

  let lastGroup = null;
  return (
    <div ref={listRef} className="absolute bottom-full mb-2 left-0 right-0 z-40 max-h-80 overflow-auto rounded-xl border border-border/50 bg-popover shadow-2xl py-1" data-testid="slash-menu">
      {filtered.map((it, i) => {
        const showHeader = it.group !== lastGroup;
        lastGroup = it.group;
        const Icon = GROUP_ICON[it.group] || Layers;
        return (
          <React.Fragment key={`${it.group}-${it.label}-${i}`}>
            {showHeader && (
              <div className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1.5">
                <Icon className="w-3 h-3" /> {it.group}
              </div>
            )}
            <button
              type="button" data-idx={i} data-testid={`slash-item-${i}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(it)}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${i === active ? 'bg-primary/10 text-foreground' : 'text-foreground/90 hover:bg-muted/30'}`}
            >
              <span className="truncate">{it.label}</span>
              {it.kind === 'fill' && <span className="ml-auto text-[9px] text-muted-foreground/60">needs a value</span>}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}
